import { useState } from "react";
import { styled } from "styled-components";
import { SkeletonPlaceholder } from "./loading/SkeletonPlaceholder";

type Props = {
  src: string;
  alt?: string;
  fallback?: React.ReactNode | string;
  className?: string;
  imageFit?: "cover" | "contain";
};

export const ImageComponent = ({
  src,
  alt,
  fallback,
  className,
  imageFit,
}: Props) => {
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading"
  );
  return (
    <ImageWrapper aria-busy={status === "loading"} className={className}>
      {(status === "loading" || (!fallback && status === "error")) && (
        <StyledSkeletonPlaceholder />
      )}
      {fallback &&
        status === "error" &&
        (typeof fallback === "string" ? (
          <Image
            src={fallback}
            alt={alt}
            $visible={status === "error"}
            $fit={imageFit}
          />
        ) : (
          <FallbackWrap>{fallback}</FallbackWrap>
        ))}
      <Image
        src={src}
        alt={alt}
        draggable={false}
        loading="lazy"
        decoding="async"
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        $visible={status === "loaded"}
        $fit={imageFit}
      />
    </ImageWrapper>
  );
};

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const FallbackWrap = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
`;

const Image = styled.img<{ $visible: boolean; $fit?: "cover" | "contain" }>`
  object-fit: ${({ $fit }) => $fit ?? "cover"};
  width: 100%;
  height: 100%;
  transition: opacity 240ms ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  display: block;
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  position: absolute;
  inset: 0;
`;
